import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Complement, ComplementGroup } from '@app/types';
import ComplementItem from './ComplementItem';
import {
  getComplementGroupSelectionType,
  getComplementGroupSubtitle,
  getErrorMessage,
  getInitialSelectedComplement,
} from './helpers';
import classes from './styles.module.css';
import { useRestaurantContext } from '@app/pages/Restaurant/context';
import CheckIcon from '@material-ui/icons/Check';
import { useMenuItemDetailsContext } from '../context';
import { Colors } from '@app/config/colors';
import menuItemDetailsClasses from '../shared/styles.module.css';
import { getAmountComplementSelected, isGroupValidOnBasisOfComplementsSelected } from '@app/utils/menu';
import cls from 'classnames';
import RequiredLabel from './RequiredLabel';
import { useDialogAlert } from '@app/components/DialogAlert';
import { useEffectAfterFirstRender } from '@app/hooks/use-effect-after-first-render';
import { useIsMobileScreenSize } from '@app/utils/media-query';

export enum ComplementGroupSelectionType {
  Select,
  Amount,
}

type Props = {
  group: ComplementGroup;
  index: number;
  isValid: boolean;
  onIsValidValueChange: (isValid: boolean) => void;
  onContainerRef: (element: HTMLElement | null) => void;
  shouldHighlightError: boolean;
  shouldAnimateError: boolean;
};

export const ERROR_COLOR = '#ef5350';

export default function ComplementGroupCard({
  group,
  index,
  isValid,
  onIsValidValueChange,
  onContainerRef,
  shouldHighlightError,
  shouldAnimateError,
}: Props) {
  const { primaryColor } = useRestaurantContext();

  const menuItemContext = useMenuItemDetailsContext();

  const isMobile = useIsMobileScreenSize();

  const [amountComplementsSelected, setAmountComplementsSelected] = useState(getAmountComplementSelected(group));
  const [complementSelected, setComplementSelected] = useState<Complement | null>(
    getInitialSelectedComplement(group.complementos)
  );

  useEffectAfterFirstRender(() => {
    const isValidGroup = isGroupValidOnBasisOfComplementsSelected(group, amountComplementsSelected);
    onIsValidValueChange(isValidGroup);
  }, [amountComplementsSelected, complementSelected]);

  const renderComplementItem = useCallback(
    (complement: Complement, index: number) => {
      return (
        <Fragment key={complement.id}>
          {index > 0 && <div className={classes.divider} />}
          <ComplementItem
            parentGroup={group}
            alreadySelectedMaxComplements={amountComplementsSelected === group.maximo}
            complement={complement}
            selected={complementSelected?.id === complement.id}
            selectionType={getComplementGroupSelectionType(group)}
            onMinusClick={handleMinusClick}
            onPlusClick={handlePlusClick}
            onSelect={handleSelect}
          />
        </Fragment>
      );

      function handleMinusClick(isHolding: boolean) {
        setAmountComplementsSelected((oldValue) => oldValue - 1);
        if (!complement.amount) complement.amount = 0;
        complement.amount--;
        menuItemContext.subtractToTotalFromComplmentPrice(complement, group);

        if (isHolding && isMobile) menuItemContext.toastAmountRef.current?.show(complement.amount);
      }

      function handlePlusClick(isHolding: boolean) {
        setAmountComplementsSelected((oldValue) => oldValue + 1);
        if (!complement.amount) complement.amount = 0;
        complement.amount++;
        menuItemContext.sumToTotalFromComplementPrice(complement, group);
        if (isHolding && isMobile) menuItemContext.toastAmountRef.current?.show(complement.amount);
      }

      function handleSelect() {
        const nextComplementSelected = complement.id === complementSelected?.id ? null : complement;
        setComplementSelected(nextComplementSelected);
        setAmountComplementsSelected(nextComplementSelected ? 1 : 0);
        complement.amount = nextComplementSelected ? 1 : 0;
        if (complementSelected) complementSelected.amount = 0;
        menuItemContext.updateTotalFromComplementSelected(nextComplementSelected, complementSelected);
      }
    },
    [complementSelected, amountComplementsSelected, group, menuItemContext, isMobile]
  );

  const isRequired = !group.opcional;

  return (
    <div ref={(ref) => onContainerRef(ref)} id={`complement-group-${index}`} style={{ paddingTop: 20 }}>
      <div
        className={cls(classes.container)}
        style={{ borderColor: Boolean(shouldHighlightError || shouldAnimateError) ? ERROR_COLOR : '#e9ecef' }}
      >
        <div className={classes.header}>
          <div className={classes.headerInfo}>
            <h3 style={{ color: primaryColor || Colors.DefaultPrimaryColor }} className={menuItemDetailsClasses.title}>
              {group.nome}
            </h3>
            <span>{getComplementGroupSubtitle(group.maximo, amountComplementsSelected)}</span>
            <br />
            {Boolean(shouldHighlightError || shouldAnimateError) && (
              <span style={{ color: ERROR_COLOR, fontSize: 12 }}>{getErrorMessage(group.minimo)}</span>
            )}
          </div>
          <div className={classes.headerStatus}>
            {isRequired && (
              <>
                {isValid ? (
                  <CheckIcon style={{ color: '#0faa11' }} />
                ) : (
                  <RequiredLabel shouldAnimateError={shouldAnimateError} shouldHighlightError={shouldHighlightError} />
                )}
              </>
            )}
          </div>
        </div>
        <div className={classes.complementsBox}>{group.complementos.map(renderComplementItem)}</div>
      </div>
    </div>
  );
}
